import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../config/URL/URL';
import {
    MonthlyRentCalculatorContent,
    MonthlyRentInputRow,
    MonthlyRentInputGroup,
    MonthlyRentLabel,
    MonthlyRentInputWrapper,
    MonthlyRentUnitText,
    MonthlyRentSelect,
    MonthlyRentInput,
    ResultDisplay,
    ResultTitle,
    ResultContent,
    ResultRow,
    ResultLabel,
    ResultValue,
    ResultHighlight,
    ResultNote,
} from './styles';
import CommonButton from '../button/CommonButton';
import { css } from '@emotion/react';
import { handleNumberInputChange, getNumericValue } from '../../../utils/calculator/numberFormatter';

const MonthlyRentCalculator = ({ checkModalBoundary, adjustModalPosition }) => {
    const [deposit, setDeposit] = useState('');
    const [jeonsePrice, setJeonsePrice] = useState('');
    const [result, setResult] = useState(null);
    const [selectedSido, setSelectedSido] = useState(null);
    const [selectedHousingType, setSelectedHousingType] = useState('');
    const [sidoList, setSidoList] = useState([]);
    const [shouldCheckBoundary, setShouldCheckBoundary] = useState(false);

    const handleJeonsePriceChange = (e) => {
        handleNumberInputChange(e, setJeonsePrice, 7);
    };

    const handleDepositChange = (e) => {
        handleNumberInputChange(e, setDeposit, 7);
    };

    const housingTypes = [
        { value: 'apartment', label: '아파트' },
        { value: 'multi_family', label: '다세대/연립' },
        { value: 'officetel', label: '오피스텔' },
    ];

    useEffect(() => {
        fetchSidoList();
    }, []);

    const fetchSidoList = async () => {
        try {
            const response = await axios.get(`${API_URL}/adminSidoList`);
            // const response = await axios.get(`http://localhost:8080/adminSidoList`);
            setSidoList(response.data);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSidoChange = (e) => {
        const selectedValue = e.target.value;
        if (selectedValue) {
            const sido = sidoList.find((item) => item.sidoNm === selectedValue);
            setSelectedSido(sido);
        } else {
            setSelectedSido(null);
        }
    };

    const handleHousingTypeChange = (e) => {
        setSelectedHousingType(e.target.value);
    };

    useEffect(() => {
        if (shouldCheckBoundary && result) {
            const timer = setTimeout(() => {
                if (checkModalBoundary()) {
                    adjustModalPosition();
                }
                setShouldCheckBoundary(false);
            }, 0);

            return () => clearTimeout(timer);
        }
    }, [result, shouldCheckBoundary, checkModalBoundary, adjustModalPosition]);

    const calculatemonthlyRent = async () => {
        if (!deposit || !jeonsePrice) {
            alert('전세가와 보증금을 모두 입력하세요.');
            return;
        }

        if (!selectedSido) {
            alert('지역을 선택하세요.');
            return;
        }

        if (!selectedHousingType) {
            alert('주택유형을 선택하세요.');
            return;
        }

        const jeonseAmount = getNumericValue(jeonsePrice) * 10000;
        const depositAmount = getNumericValue(deposit) * 10000;

        // 음수 체크
        if (jeonseAmount < 0) {
            alert('전세가는 음수일 수 없습니다.');
            return;
        }

        if (depositAmount < 0) {
            alert('보증금은 음수일 수 없습니다.');
            return;
        }

        // 비현실적으로 큰 금액 체크 (1000억원 이상)
        if (jeonseAmount >= 100000000000) {
            alert('전세가가 너무 큽니다. 다시 확인해주세요.');
            return;
        }

        if (depositAmount >= 100000000000) {
            alert('보증금이 너무 큽니다. 다시 확인해주세요.');
            return;
        }

        // 전세가가 보증금보다 작은 경우 체크
        if (jeonseAmount <= depositAmount) {
            alert('전세가는 보증금보다 커야 합니다.');
            return;
        }

        const response = await axios.get(`${API_URL}/calc/rentRatio/${selectedSido.sidoCd}`);
        // const response = await axios.get(`http://localhost:8080/calc/rentRatio/${selectedSido.sidoCd}`);
        const data = response.data;
        let rentRatio = 0;

        switch (selectedHousingType) {
            case 'apartment':
                rentRatio = data.apt;
                break;
            case 'multi_family':
                rentRatio = data.mhou;
                break;
            case 'officetel':
                rentRatio = data.officetel;
        }

        // 월세 계산: ((전세가 - 보증금) * 전세환산율) / 12
        const monthlyRent = Math.floor(((jeonseAmount - depositAmount) * (rentRatio / 100)) / 12);

        const calculatedResult = {
            deposit: depositAmount,
            jeonsePrice: jeonseAmount,
            rentRatio: rentRatio,
            monthlyRent: monthlyRent,
            sido: selectedSido.sidoNm,
            housingType: housingTypes.find((item) => item.value === selectedHousingType).label,
        };

        setResult(calculatedResult);
        setShouldCheckBoundary(true);
    };

    const clearAll = () => {
        setDeposit('');
        setJeonsePrice('');
        setSelectedHousingType('');
        setSelectedSido(null);
        setResult(null);
    };

    return (
        <MonthlyRentCalculatorContent>
            <MonthlyRentInputRow>
                <MonthlyRentInputGroup>
                    <MonthlyRentLabel>
                        <span>지역</span>
                        <span>*</span>
                    </MonthlyRentLabel>
                    <MonthlyRentSelect
                        value={selectedSido ? selectedSido.sidoNm : ''}
                        onChange={handleSidoChange}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <option value="">시도 선택</option>
                        {sidoList.map((sido) => (
                            <option key={sido.sidoCd} value={sido.sidoNm}>
                                {sido.sidoNm}
                            </option>
                        ))}
                    </MonthlyRentSelect>
                </MonthlyRentInputGroup>

                <MonthlyRentInputGroup>
                    <MonthlyRentLabel>
                        <span>주택유형</span>
                        <span>*</span>
                    </MonthlyRentLabel>
                    <MonthlyRentSelect
                        value={selectedHousingType}
                        onChange={handleHousingTypeChange}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <option value="">주택유형 선택</option>
                        {housingTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </MonthlyRentSelect>
                </MonthlyRentInputGroup>
            </MonthlyRentInputRow>

            <MonthlyRentInputRow>
                <MonthlyRentInputGroup>
                    <MonthlyRentLabel>
                        <span>전세가</span>
                        <span>*</span>
                    </MonthlyRentLabel>
                    <MonthlyRentInputWrapper>
                        <MonthlyRentInput
                            type="text"
                            placeholder="전세가 입력"
                            value={jeonsePrice}
                            onChange={handleJeonsePriceChange}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <MonthlyRentUnitText>만원</MonthlyRentUnitText>
                    </MonthlyRentInputWrapper>
                </MonthlyRentInputGroup>

                <MonthlyRentInputGroup>
                    <MonthlyRentLabel>
                        <span>보증금</span>
                        <span>*</span>
                    </MonthlyRentLabel>
                    <MonthlyRentInputWrapper>
                        <MonthlyRentInput
                            type="text"
                            placeholder="보증금 입력"
                            value={deposit}
                            onChange={handleDepositChange}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <MonthlyRentUnitText>만원</MonthlyRentUnitText>
                    </MonthlyRentInputWrapper>
                </MonthlyRentInputGroup>
            </MonthlyRentInputRow>

            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                <CommonButton
                    variant="secondary"
                    size="medium"
                    onClick={calculatemonthlyRent}
                    customStyles={css`
                        flex: 1;
                        border: none;

                        &:hover {
                            transform: translateY(-2px);
                        }
                    `}
                >
                    월세 계산하기
                </CommonButton>

                <CommonButton
                    variant="outlined"
                    size="medium"
                    onClick={clearAll}
                    customStyles={css`
                        flex: 1;
                    `}
                >
                    초기화
                </CommonButton>
            </div>
            {result && (
                <ResultDisplay>
                    <ResultTitle>계산 결과</ResultTitle>
                    <ResultContent>
                        <ResultRow>
                            <ResultLabel>
                                {result.sido} {result.housingType} 전세환산율
                            </ResultLabel>
                            <ResultValue>{result.rentRatio}%</ResultValue>
                        </ResultRow>
                        <ResultRow>
                            <ResultLabel>보증금</ResultLabel>
                            <ResultValue>{result.deposit.toLocaleString()}원</ResultValue>
                        </ResultRow>
                        <ResultRow>
                            <ResultLabel>월세</ResultLabel>
                            <ResultHighlight>{result.monthlyRent.toLocaleString()}원</ResultHighlight>
                        </ResultRow>
                    </ResultContent>
                    <ResultNote>
                        전세가환산율 기준시점은 한국감정원 최종공표 기준 2025년 5월입니다.
                        <br />
                        본 결과는 참고용으로, 실제 월세액과 다소 차이가 있을 수 있습니다.
                        <br />
                        산출방식:((전세가-보증금)x전세환산율)/12(개월)
                    </ResultNote>
                </ResultDisplay>
            )}
        </MonthlyRentCalculatorContent>
    );
};

export default MonthlyRentCalculator;
